import { TailIcon } from '@/common/icons/TailIcon';
import { GeneralTable } from '@/components/tables/general';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion.tsx';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area.tsx';
import { IContourAction } from '@/features/stewardship/models';
import { cn } from '@/lib/utils.ts';
import { ColumnDef } from '@tanstack/react-table';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AgroEventsProjectContoursResponse } from '../../models/IAgroTechnicalEvents';
import { TableLoading } from './TableLoading';

interface AgrotechContourProps {
  contours: AgroEventsProjectContoursResponse[];
  activeContour: string;
  onContourChange: (value: string) => void;
  agroEvents: IContourAction[];
  eventsLoading: boolean;
  columns: ColumnDef<IContourAction, any>[];
  pageSize: number;
  currentPage: number;
  onRowClick: (row: IContourAction) => void;
}

export const AgrotechContour: FC<AgrotechContourProps> = ({
  contours,
  activeContour,
  onContourChange,
  agroEvents,
  eventsLoading,
  columns,
  pageSize,
  currentPage,
  onRowClick,
}) => {
  const { t } = useTranslation();

  return (
    <ScrollArea className="relative h-full w-full flex-1">
      {contours.length === 0 ? (
        <div className="flex h-full w-full items-center justify-center text-sm text-gray-500">
          {t('nothing.found')}
        </div>
      ) : (
        <Accordion
          collapsible
          type="single"
          className="w-full min-w-[900px] rounded-xl border overflow-hidden"
          onValueChange={(value) => {
            if (value) onContourChange(value);
          }}
        >
          {contours.map((item) => (
            <AccordionItem
              key={item?.contour_id}
              value={item?.contour_id}
              className={cn('mb-0 rounded-none border-0 shadow-none')}
            >
              <AccordionTrigger
                className="[&>svg]:text-green-600 group w-full"
                childrenClassName="w-full bg-[#0A0F290A] rounded-md px-2"
              >
                <div className="flex w-full items-center justify-between p-3 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <p className="text-[13px] font-medium">
                        {item?.contour_number}
                      </p>
                      <TailIcon />
                    </div>
                    <p className="text-[13px] font-normal">
                      {t('area_with_colon')} {item?.area} ga
                    </p>
                    {+item?.action_count > 0 && (
                      <Badge
                        variant="destructive"
                        className="h-5 min-w-5 bg-red-500 text-white rounded-full px-1 text-xs tabular-nums"
                      >
                        {item?.action_count}
                      </Badge>
                    )}
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent className="flex flex-col gap-4 text-balance">
                {eventsLoading ? (
                  <TableLoading />
                ) : (
                  <div className="rounded-xl border">
                    <GeneralTable
                      columns={columns}
                      data={agroEvents}
                      pageSize={pageSize}
                      bodyClass="bg-white"
                      headerClass="rounded-xl"
                      rowsClassName="bg-white"
                      loading={eventsLoading}
                      currentPage={currentPage}
                      onRowClick={(row) => onRowClick(row)}
                    />
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      <ScrollBar orientation="horizontal" className="top-0" />
      <ScrollBar />
    </ScrollArea>
  );
};
